import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { authAPI } from '../services/api';
import { AuthShell } from './auth/AuthShell';
import { Label } from '../components/ui/input';
import { PasswordInput } from '../components/ui/PasswordInput';
import { Button } from '../components/ui/button';
import { Rise } from '../components/ui/motion';
import { toast } from 'react-toastify';

const ResetPassword: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const email = searchParams.get('email');
  const navigate = useNavigate();

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!token || !email) {
      // One toast only: React StrictMode runs this effect twice in
      // development, and once the container is mounted (client-side
      // navigation) every call becomes its own toast without an id.
      toast.error('Invalid or missing password reset link', { toastId: 'reset-link-invalid' });
      navigate('/login');
    }
  }, [token, email, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters long');
      return;
    }

    setLoading(true);
    try {
      await authAPI.resetPassword({ email, token, newPassword });
      toast.success('Password reset successfully! Please log in.');
      navigate('/login');
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  if (!token || !email) {
    return null; // Don't render if redirecting
  }

  return (
    <AuthShell title="Create new password" subtitle="Please enter your new password below.">
      <form onSubmit={handleSubmit} className="login-form">
        <Rise as="div" index={2} className="form-group">
          <Label htmlFor="newPassword">New Password</Label>
          <PasswordInput
            id="newPassword"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            required
            placeholder="••••••••"
            minLength={8}
          />
        </Rise>

        <Rise as="div" index={3} className="form-group">
          <Label htmlFor="confirmPassword">Confirm Password</Label>
          <PasswordInput
            id="confirmPassword"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            placeholder="••••••••"
            minLength={8}
          />
        </Rise>

        <Rise as="div" index={4}>
          <Button
            type="submit"
            variant="default"
            size="lg"
            className="login-btn"
            disabled={loading}
          >
            {loading ? 'Saving…' : 'Reset Password'}
          </Button>
        </Rise>
      </form>
    </AuthShell>
  );
};

export default ResetPassword;
